import React, { createContext, useContext, useEffect, useState } from 'react';

export type Market = 'US' | 'IN';

interface MarketContextValue {
  market: Market;
  isLoading: boolean;
}

const MarketContext = createContext<MarketContextValue | undefined>(undefined);

let marketRequest: Promise<Market> | null = null;

const detectMarket = async (): Promise<Market> => {
  // trim() tolerates shell quirks like cmd's `set VAR=IN && ...` adding a trailing space
  const configuredMarket = process.env.REACT_APP_MARKET?.trim().toUpperCase();
  if (configuredMarket === 'IN' || configuredMarket === 'US') {
    return configuredMarket;
  }

  try {
    const response = await fetch('/market.php', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      return 'US';
    }

    const data = await response.json();
    return data.country === 'IN' ? 'IN' : 'US';
  } catch {
    return 'US';
  }
};

export const MarketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [market, setMarket] = useState<Market>('US');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!marketRequest) {
      marketRequest = detectMarket();
    }

    marketRequest.then((detectedMarket) => {
      setMarket(detectedMarket);
      setIsLoading(false);
    });
  }, []);

  return (
    <MarketContext.Provider value={{ market, isLoading }}>
      {children}
    </MarketContext.Provider>
  );
};

export const useMarket = (): MarketContextValue => {
  const context = useContext(MarketContext);

  if (!context) {
    throw new Error('useMarket must be used within a MarketProvider');
  }

  return context;
};
